import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import type { CandidateUser, PortalSession } from "@/lib/api";

const SESSION_STORAGE_KEY = "gupio_portal_session";

interface SessionContextValue {
  clearSession: () => void;
  isReady: boolean;
  session: PortalSession | null;
  setSession: (nextSession: PortalSession) => void;
  updateCandidate: (candidate: CandidateUser) => void;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<PortalSession | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(SESSION_STORAGE_KEY);

      if (stored) {
        const parsed = JSON.parse(stored) as PortalSession;

        if (Date.parse(parsed.expiresAt) > Date.now()) {
          setSessionState(parsed);
        } else {
          window.localStorage.removeItem(SESSION_STORAGE_KEY);
        }
      }
    } catch {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
    } finally {
      setIsReady(true);
    }
  }, []);

  function setSession(nextSession: PortalSession) {
    setSessionState(nextSession);
    window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(nextSession));
  }

  function clearSession() {
    setSessionState(null);
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
  }

  function updateCandidate(candidate: CandidateUser) {
    setSessionState((current) => {
      if (!current || current.role !== "candidate") {
        return current;
      }

      const nextSession: PortalSession = {
        ...current,
        user: candidate
      };

      window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(nextSession));
      return nextSession;
    });
  }

  const value = useMemo<SessionContextValue>(
    () => ({
      clearSession,
      isReady,
      session,
      setSession,
      updateCandidate
    }),
    [isReady, session]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const context = useContext(SessionContext);

  if (!context) {
    throw new Error("useSession must be used within SessionProvider.");
  }

  return context;
}
